import { useTranslation } from 'react-i18next';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { useUserPublicProfile } from '@user/PublicProfile/useUserPublicProfile';
import { generateDefaultUserIcon } from '@framework/utils';
import { CommentConstants } from '../constants';
import { formatRelativeTime } from '@framework/i18n/formatRelativeTime';
import { MODEL_COMMENT_RELATIVE_TIME_I18N_KEYS } from './ModelCommentItem/modelCommentRelativeTimeI18nKeys';
import { useNow } from '@framework/hooks/useNow';
import type { ModelCommentThread } from '../domain';

// 解決済みを示す円＋チェックマークのバッジ
const RESOLVED_ICON_SIZE = 32;
const RESOLVED_R = RESOLVED_ICON_SIZE / 2;
const CHECK_FIT_SIZE = RESOLVED_ICON_SIZE * 0.45;
const CHECK_ICON_W = faCheck.icon[0] as number;
const CHECK_ICON_H = faCheck.icon[1] as number;
const CHECK_SCALE = Math.min(CHECK_FIT_SIZE / CHECK_ICON_W, CHECK_FIT_SIZE / CHECK_ICON_H);
const CHECK_OFFSET_X = RESOLVED_R - (CHECK_ICON_W * CHECK_SCALE) / 2;
const CHECK_OFFSET_Y = RESOLVED_R - (CHECK_ICON_H * CHECK_SCALE) / 2;

const ResolvedCheckIcon: React.FC = () => (
  <div className="absolute right-4 top-4">
    <svg width={RESOLVED_ICON_SIZE} height={RESOLVED_ICON_SIZE}>
      <circle cx={RESOLVED_R} cy={RESOLVED_R} r={RESOLVED_R - 1.5} fill="#0097A7" />
      <g transform={`translate(${CHECK_OFFSET_X}, ${CHECK_OFFSET_Y}) scale(${CHECK_SCALE})`}>
        <path d={faCheck.icon[4] as string} fill="white" />
      </g>
    </svg>
  </div>
);

const { width } = CommentConstants.UISetting;
const PREVIEW_HEIGHT = 280;
const AVATAR_SIZE = 36;
const AVATAR_PADDING = 6;
// 未読バッジ
const BADGE_R = 10;
const BADGE_COLOR = '#4299e1';

type Props = {
  x: number;
  y: number;
  isRead: boolean;
  commentThread: ModelCommentThread;
};

export const PrimaryCommentPreview: React.FC<Props> = ({ x, y, isRead, commentThread }: Props) => {
  const { t } = useTranslation();
  const now = useNow();
  const primaryComment = commentThread.getPrimaryComment();
  const profile = useUserPublicProfile(primaryComment.userId);

  if (!profile) return null;

  const postAt = formatRelativeTime(primaryComment.getPostAt(), now, t, MODEL_COMMENT_RELATIVE_TIME_I18N_KEYS);
  const body = primaryComment.getBody();
  const replyCount = commentThread.getReplyComments().length;
  const isResolved = commentThread.isResolved();

  // 未読バッジの中心: プレビュー右上の角に重なる位置
  const badgeCx = x + width - BADGE_R / 2;
  const badgeCy = y + BADGE_R / 2;

  return (
    <>
      <foreignObject x={x} y={y} width={width} height={PREVIEW_HEIGHT} pointerEvents="none">
        <div
          className="relative flex flex-col overflow-hidden rounded-2xl border-4 border-[#d1d5db] bg-white p-4 text-3xl"
          style={{ width: width, height: PREVIEW_HEIGHT, boxSizing: 'border-box' }}
        >
          {/* 解決済みアイコンを右上に絶対配置 */}
          {isResolved && <ResolvedCheckIcon />}

          {/* ヘッダー: ModelCommentHeader と同スタイル */}
          <div className="mb-2 flex items-center">
            <div
              style={{
                padding: AVATAR_PADDING,
                width: AVATAR_SIZE + AVATAR_PADDING * 2,
                height: AVATAR_SIZE + AVATAR_PADDING * 2,
                flexShrink: 0,
              }}
            >
              {profile.imageUrl ? (
                <img
                  className="max-w-none rounded-full bg-neutral-300 object-cover"
                  src={profile.imageUrl}
                  alt={profile.name}
                  style={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
                />
              ) : (
                <div
                  className="max-w-none overflow-hidden rounded-full bg-neutral-300"
                  style={{ width: AVATAR_SIZE, height: AVATAR_SIZE }}
                >
                  {generateDefaultUserIcon({
                    id: profile.id,
                    name: profile.name,
                    isAnonymous: profile.isAnonymous,
                    size: AVATAR_SIZE,
                  })}
                </div>
              )}
            </div>
            <div className="overflow-hidden text-ellipsis pl-[6px] text-3xl font-bold">{profile.name}</div>
            <div className="pl-3 text-2xl text-gray-600">{postAt}</div>
          </div>

          {/* 本文: 折り返しあり・最大3行・末尾を ... で省略 */}
          <div
            className="text-3xl"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}
          >
            {body}
          </div>
          {/* 返信件数: リプライがある場合のみ左下に表示 */}
          {replyCount > 0 && (
            <div className="mt-auto pt-2 text-2xl text-gray-600">
              {t('canvas.comment.reply.count', { count: replyCount })}
            </div>
          )}
        </div>
      </foreignObject>

      {/* 未読バッジ */}
      {!isRead && (
        <circle
          cx={badgeCx}
          cy={badgeCy}
          r={BADGE_R}
          fill={BADGE_COLOR}
          stroke="white"
          strokeWidth={3}
          pointerEvents="none"
        />
      )}
    </>
  );
};
